/**
 * Swirl Header Component
 * Displays title, gradient, creation date, and energy score
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { harmonizeColorPalette, generateGradientCSS } from '../../utils/visualHarmony';

export default function SwirlHeader({ swirl }) {
  const navigate = useNavigate();
  const [durationAlive, setDurationAlive] = useState('');
  const [energyScore, setEnergyScore] = useState(0);
  const [gradientPosition, setGradientPosition] = useState(0);

  useEffect(() => {
    if (swirl?.created_at) {
      const created = new Date(swirl.created_at);
      const now = new Date();
      const diffMs = now - created;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays < 7) {
        setDurationAlive(`${diffDays} day${diffDays !== 1 ? 's' : ''}`);
      } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        setDurationAlive(`${weeks} week${weeks !== 1 ? 's' : ''}`);
      } else {
        const months = Math.floor(diffDays / 30);
        setDurationAlive(`${months} month${months !== 1 ? 's' : ''}`);
      }
    }

    // Calculate energy score (similarity_score * engagement)
    if (swirl?.similarity_score !== null && swirl?.similarity_score !== undefined) {
      const itemCount = Array.isArray(swirl.item_ids) ? swirl.item_ids.length : 0;
      const engagement = Math.min(1, itemCount / 20); // Normalize to 0-1
      const energy = Math.round((swirl.similarity_score * engagement) * 100);
      setEnergyScore(energy);
    }
  }, [swirl]);

  // Animate gradient drift
  useEffect(() => {
    const interval = setInterval(() => {
      setGradientPosition(prev => (prev + 0.5) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  if (!swirl) return null;

  const colorPalette = harmonizeColorPalette(swirl.color_palette);
  const gradientColors = Array.isArray(colorPalette.gradient) && colorPalette.gradient.length >= 2
    ? colorPalette.gradient
    : [colorPalette.primary || '#A78BFA', '#C084FC'];

  return (
    <div 
      className="relative rounded-2xl overflow-hidden mb-6"
      style={{
        background: generateGradientCSS(gradientColors, `${135 + gradientPosition * 0.1}deg`),
      }}
    >
      {/* Animated gradient overlay */}
      <motion.div
        className="absolute inset-0 opacity-20"
        animate={{
          background: `linear-gradient(${gradientPosition}deg, transparent, ${colorPalette.primary}40, transparent)`,
        }}
        transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
      />

      <div className="relative z-10 p-8">
        {/* Back button */}
        <button
          onClick={() => navigate('/swirlspace')}
          className="mb-4 flex items-center gap-2 text-white/90 hover:text-white transition-colors"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back to Swirlspace</span>
        </button>

        {/* Title and metadata */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-4xl font-semibold text-white mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
              {swirl.title}
            </h1>
            {swirl.description && (
              <p className="text-white/80 text-lg mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                {swirl.description}
              </p>
            )}
            
            <div className="flex items-center gap-4 text-white/90">
              {durationAlive && (
                <span className="text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Created {durationAlive} ago
                </span>
              )}
              <span className="text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                {swirl.item_ids?.length || 0} items
              </span>
            </div>
          </div>

          {/* Energy Score Indicator */}
          <motion.div
            className="flex flex-col items-center bg-white/20 backdrop-blur-sm px-4 py-3 rounded-xl"
            animate={{
              boxShadow: [
                `0 0 10px ${colorPalette.primary}40`,
                `0 0 20px ${colorPalette.primary}60`,
                `0 0 10px ${colorPalette.primary}40`,
              ],
            }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-white" />
              <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
                {energyScore}
              </span>
            </div>
            <span className="text-xs text-white/80" style={{ fontFamily: 'Inter, sans-serif' }}>
              Energy
            </span>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

